import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  Check,
  CheckCircle2,
  CreditCard,
  FileText,
  Folder,
  Hash,
  Home,
  Landmark,
  Loader2,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { customersService } from '../../services/customers.service';
import { useDocumentExtraction } from '../../hooks/useDocumentExtraction';

/* ── document type config ────────────────────────────────────────────────────── */
const DOC_CONFIG = {
  PASSPORT: {
    label: 'Passport', icon: BookOpen, color: 'text-accent-600', bg: 'bg-accent-50',
    accept: '.pdf,.jpg,.jpeg,.png',
    fields: ['documentNumber', 'issuingCountry', 'issueDate', 'expiryDate'],
    labels: { documentNumber: 'Passport number', issuingCountry: 'Issuing country', issueDate: 'Issue date', expiryDate: 'Expiry date' },
    required: ['documentNumber', 'issuingCountry'],
    placeholders: { documentNumber: 'e.g. AB1234567' },
  },
  NATIONAL_ID: {
    label: 'National ID', icon: CreditCard, color: 'text-info-600', bg: 'bg-info-50',
    accept: '.pdf,.jpg,.jpeg,.png',
    fields: ['documentNumber', 'issuingCountry', 'issueDate', 'expiryDate'],
    labels: { documentNumber: 'ID number', issuingCountry: 'Issuing country', issueDate: 'Issue date', expiryDate: 'Expiry date' },
    required: ['documentNumber', 'issuingCountry'],
    placeholders: { documentNumber: 'e.g. 784-1234-5678901-2' },
  },
  BANK_STATEMENT: {
    label: 'Bank Statement', icon: Landmark, color: 'text-success-600', bg: 'bg-success-50',
    accept: '.pdf,.jpg,.jpeg,.png',
    fields: ['issueDate'],
    labels: { issueDate: 'Statement date' },
    required: [],
    placeholders: {},
  },
  PROOF_OF_ADDRESS: {
    label: 'Proof of Address', icon: Home, color: 'text-warning-600', bg: 'bg-warning-50',
    accept: '.pdf,.jpg,.jpeg,.png',
    fields: ['issueDate', 'issuingCountry'],
    labels: { issueDate: 'Document date', issuingCountry: 'Country of issue' },
    required: [],
    placeholders: {},
  },
  TAX_ID: {
    label: 'Tax ID', icon: Hash, color: 'text-purple-600', bg: 'bg-purple-50',
    accept: '.pdf,.jpg,.jpeg,.png',
    fields: ['documentNumber'],
    labels: { documentNumber: 'Tax ID / TIN' },
    required: [],
    placeholders: { documentNumber: 'e.g. 123-45-6789' },
  },
  W8: {
    label: 'W-8BEN', icon: FileText, color: 'text-ink-600', bg: 'bg-ink-50',
    accept: '.pdf',
    fields: [],
    labels: {},
    required: [],
    placeholders: {},
  },
  W9: {
    label: 'W-9', icon: FileText, color: 'text-ink-600', bg: 'bg-ink-50',
    accept: '.pdf',
    fields: [],
    labels: {},
    required: [],
    placeholders: {},
  },
  LIVNESS_VERIFICATION_VIDEO: {
    label: 'Liveness Video', icon: Video, color: 'text-pink-600', bg: 'bg-pink-50',
    accept: '.mp4,.mov,.webm',
    fields: [],
    labels: {},
    required: [],
    placeholders: {},
  },
  OTHER: {
    label: 'Other', icon: Folder, color: 'text-ink-500', bg: 'bg-ink-50',
    accept: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
    fields: ['documentNumber'],
    labels: { documentNumber: 'Reference / number' },
    required: [],
    placeholders: { documentNumber: 'Optional reference' },
  },
};

/* ── countries ───────────────────────────────────────────────────────────────── */
const COUNTRIES = [
  { code: 'DZ', name: 'Algeria' }, { code: 'AR', name: 'Argentina' },
  { code: 'AU', name: 'Australia' }, { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' }, { code: 'BE', name: 'Belgium' },
  { code: 'BR', name: 'Brazil' }, { code: 'CA', name: 'Canada' },
  { code: 'CN', name: 'China' }, { code: 'DK', name: 'Denmark' },
  { code: 'EG', name: 'Egypt' }, { code: 'ET', name: 'Ethiopia' },
  { code: 'FR', name: 'France' }, { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' }, { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' }, { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' }, { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' }, { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' }, { code: 'KE', name: 'Kenya' },
  { code: 'KW', name: 'Kuwait' }, { code: 'LB', name: 'Lebanon' },
  { code: 'LY', name: 'Libya' }, { code: 'MY', name: 'Malaysia' },
  { code: 'MA', name: 'Morocco' }, { code: 'MX', name: 'Mexico' },
  { code: 'NL', name: 'Netherlands' }, { code: 'NZ', name: 'New Zealand' },
  { code: 'NG', name: 'Nigeria' }, { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' }, { code: 'PK', name: 'Pakistan' },
  { code: 'PH', name: 'Philippines' }, { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' }, { code: 'RU', name: 'Russia' },
  { code: 'SA', name: 'Saudi Arabia' }, { code: 'SG', name: 'Singapore' },
  { code: 'ZA', name: 'South Africa' }, { code: 'KR', name: 'South Korea' },
  { code: 'ES', name: 'Spain' }, { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' }, { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' }, { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' }, { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' }, { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' }, { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' }, { code: 'YE', name: 'Yemen' },
];

/* ── helpers ─────────────────────────────────────────────────────────────────── */
const MAX_MB = 10;
const extFromFile = (file) => { const p = file.name.split('.'); return p.length > 1 ? p.pop().toLowerCase() : 'pdf'; };
const fmtBytes = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
const isImage = (f) => f && /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name);
const isPdf = (f) => f && /\.pdf$/i.test(f.name);
const isVideo = (f) => f && /\.(mp4|mov|webm|avi)$/i.test(f.name);
const isWordDoc = (f) => f && /\.(doc|docx)$/i.test(f.name);
const canOcr = (f) => f && isImage(f);

/* ── confidence badge ────────────────────────────────────────────────────────── */
const ConfidenceBadge = ({ confidence, isManual }) => {
  if (isManual) {
    return (
      <span className="inline-flex items-center gap-1 ml-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-ink-400 inline-block" />
        <span className="text-2xs text-ink-400">manual</span>
      </span>
    );
  }
  if (confidence === undefined || confidence === null) return null;
  const pct = Math.round(confidence * 100);
  if (confidence >= 0.85) {
    return (
      <span className="inline-flex items-center gap-1 ml-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-success-500 inline-block" />
        <span className="text-2xs text-success-600">{pct}%</span>
      </span>
    );
  }
  if (confidence >= 0.60) {
    return (
      <span className="inline-flex items-center gap-1 ml-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-warning-500 inline-block" />
        <span className="text-2xs text-warning-600">{pct}%</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 ml-1.5">
      <span className="h-1.5 w-1.5 rounded-full bg-danger-500 inline-block" />
      <span className="text-2xs text-danger-600">{pct}%</span>
    </span>
  );
};

/* ── extraction status bar ───────────────────────────────────────────────────── */
const ExtractionBar = ({ status, progress }) => {
  if (status === 'idle' || status === 'error') return null;
  const labels = {
    preprocessing: 'Preprocessing image…',
    ocr: `Running OCR (${progress}%)…`,
    extracting: 'Extracting fields…',
    done: 'Extraction complete',
  };
  const label = labels[status] || '';
  const pct = status === 'done' ? 100 : status === 'extracting' ? 95 : status === 'preprocessing' ? 5 : progress;

  return (
    <div className="mt-2 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-2xs text-ink-600 font-medium flex items-center gap-1.5">
          {status !== 'done' && <Loader2 className="h-3 w-3 animate-spin text-accent-600" />}
          {status === 'done' && <CheckCircle2 className="h-3 w-3 text-success-600" />}
          {label}
        </span>
        <span className="text-2xs text-ink-400 tnum">{pct}%</span>
      </div>
      <div className="h-1 bg-ink-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${status === 'done' ? 'bg-success-500' : 'bg-accent-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

/* ── file preview ────────────────────────────────────────────────────────────── */
const FilePreview = ({ file, onRemove, disabled }) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!file || (!isImage(file) && !isPdf(file) && !isVideo(file))) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!file) return null;

  if (isImage(file) && blobUrl) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-ink-200 group">
        <img
          src={blobUrl}
          alt="Document preview"
          className="w-full h-48 object-contain bg-ink-900"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5 pt-6">
          <p className="text-xs font-medium text-white truncate">{file.name}</p>
          <p className="text-2xs text-white/60 mt-0.5">{fmtBytes(file.size)}</p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 h-6 w-6 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-ink-700 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  if (isPdf(file) && blobUrl) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-ink-200">
        <iframe
          src={blobUrl}
          title="PDF preview"
          className="w-full border-0"
          style={{ height: '200px' }}
        />
        <span className="absolute top-2 left-2 bg-danger-600 text-white text-2xs font-bold px-1.5 py-0.5 rounded">
          PDF
        </span>
        <div className="px-3 py-2 bg-ink-50 border-t border-ink-200">
          <p className="text-xs font-medium text-ink-900 truncate">{file.name}</p>
          <p className="text-2xs text-ink-400 mt-0.5">{fmtBytes(file.size)}</p>
        </div>
      </div>
    );
  }

  if (isVideo(file) && blobUrl) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-ink-200 group">
        <video
          src={blobUrl}
          controls
          muted
          className="w-full max-h-48 bg-ink-900"
        >
          <track kind="captions" />
        </video>
        <div className="px-3 py-2 bg-ink-50 border-t border-ink-200">
          <p className="text-xs font-medium text-ink-900 truncate">{file.name}</p>
          <p className="text-2xs text-ink-400 mt-0.5">{fmtBytes(file.size)}</p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 h-6 w-6 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-ink-700 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  if (isWordDoc(file)) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-ink-200 bg-ink-50 px-4 py-4">
        <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink-900 truncate">{file.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-ink-400">{fmtBytes(file.size)}</span>
            <span className="h-1 w-1 rounded-full bg-ink-300" />
            <span className="text-2xs font-semibold text-blue-600 uppercase tracking-wider">Word Document</span>
          </div>
        </div>
        {!disabled && (
          <button type="button" onClick={onRemove} className="p-1 text-ink-400 hover:text-ink-700 shrink-0">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-xl border border-ink-200 bg-ink-50 px-4 py-4">
      <div className="h-11 w-11 rounded-xl bg-info-100 flex items-center justify-center shrink-0">
        <FileText className="h-5 w-5 text-info-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink-900 truncate">{file.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-ink-400">{fmtBytes(file.size)}</span>
          <span className="h-1 w-1 rounded-full bg-ink-300" />
          <span className="text-2xs font-semibold text-ink-400 uppercase tracking-wider">Document</span>
        </div>
      </div>
      {!disabled && (
        <button type="button" onClick={onRemove} className="p-1 text-ink-400 hover:text-ink-700 shrink-0">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

/* ── drop zone (empty state) ─────────────────────────────────────────────────── */
const DropZone = ({ onFile, accept, disabled, dragging, setDragging, inputRef }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => !disabled && inputRef.current?.click()}
    onKeyDown={(e) => e.key === 'Enter' && !disabled && inputRef.current?.click()}
    onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
    onDragLeave={() => setDragging(false)}
    onDrop={(e) => {
      e.preventDefault(); setDragging(false);
      if (!disabled) { const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }
    }}
    className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed h-44 cursor-pointer transition-all select-none
      ${dragging ? 'border-accent-400 bg-accent-50 scale-[1.01]' : 'border-ink-200 hover:border-accent-300 hover:bg-accent-50/50'}
      ${disabled ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
  >
    <div className="h-12 w-12 rounded-full bg-ink-100 flex items-center justify-center mb-3">
      <Upload className="h-5 w-5 text-ink-400" />
    </div>
    <p className="text-sm font-medium text-ink-700">
      Drop file here or <span className="text-accent-600 underline-offset-2 hover:underline">browse</span>
    </p>
    <p className="text-xs text-ink-400 mt-1.5">{accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ')} — max {MAX_MB} MB</p>
    <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} disabled={disabled} />
  </div>
);

/* ── country combobox ────────────────────────────────────────────────────────── */
const CountryCombobox = ({ value, onChange }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const selectedCountry = COUNTRIES.find((c) => c.code === value) || null;

  const filtered = query.trim() === ''
    ? COUNTRIES
    : COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.code.toLowerCase().includes(query.toLowerCase())
      );

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        if (!selectedCountry) setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedCountry]);

  useEffect(() => {
    if (listRef.current && activeIdx >= 0) {
      const item = listRef.current.children[activeIdx];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    setActiveIdx(-1);
  };

  const handleSelect = (country) => {
    setQuery('');
    setOpen(false);
    setActiveIdx(-1);
    onChange(country.code);
  };

  const handleClear = () => {
    setQuery('');
    setOpen(false);
    setActiveIdx(-1);
    onChange('');
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      setActiveIdx(0);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      if (!selectedCountry) setQuery('');
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0 && filtered[activeIdx]) {
      e.preventDefault();
      handleSelect(filtered[activeIdx]);
    }
  };

  const inputValue = open ? query : (selectedCountry ? selectedCountry.name : query);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => { setOpen(true); if (selectedCountry) setQuery(''); }}
          onKeyDown={handleKeyDown}
          placeholder="Search countries…"
          className="input text-sm w-full pr-7"
        />
        {(value || query) && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear selection"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-10 bottom-full mb-1 w-full bg-white border border-ink-200 rounded-xl shadow-popover max-h-48 overflow-y-auto"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-400">No matches</li>
          ) : (
            filtered.map((c, i) => (
              <li
                key={c.code}
                role="option"
                aria-selected={value === c.code}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
                className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between
                  ${i === activeIdx ? 'bg-accent-50 text-accent-700' : 'hover:bg-ink-50 text-ink-900'}
                  ${value === c.code ? 'font-medium' : ''}`}
              >
                <span>{c.name}</span>
                <span className="text-xs text-ink-400 ml-2">{c.code}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

/* ── metadata fields ─────────────────────────────────────────────────────────── */
const MetadataFields = ({ docType, values, onChange, extractedFields: extractedFieldsProp, manuallyEditedFields }) => {
  const cfg = DOC_CONFIG[docType];
  if (!cfg) return null;

  const ef = extractedFieldsProp || {};
  const editedFields = manuallyEditedFields || new Set();

  const getBadgeProps = (fieldName) => {
    if (editedFields.has(fieldName)) return { isManual: true };
    const fieldData = ef[fieldName];
    if (fieldData?.confidence !== undefined) return { confidence: fieldData.confidence };
    return {};
  };

  if (cfg.fields.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[9rem] rounded-xl border border-success-200 bg-success-50 px-4 py-6 text-center gap-2">
        <div className="h-9 w-9 rounded-full bg-success-100 flex items-center justify-center">
          <Check className="h-4 w-4 text-success-600" />
        </div>
        <p className="text-sm font-medium text-success-800">No additional information required</p>
        <p className="text-xs text-success-600">Just upload the file and you're done.</p>
      </div>
    );
  }

  const needsCountry = cfg.fields.includes('issuingCountry');
  const needsNumber = cfg.fields.includes('documentNumber');
  const needsIssue = cfg.fields.includes('issueDate');
  const needsExpiry = cfg.fields.includes('expiryDate');
  const isRequired = (f) => cfg.required.includes(f);

  return (
    <div className="space-y-3">
      {needsNumber && (
        <div>
          <label className="field-label">
            {cfg.labels.documentNumber}
            {isRequired('documentNumber') && <span className="text-danger-500 ml-0.5">*</span>}
            <ConfidenceBadge {...getBadgeProps('documentNumber')} />
          </label>
          <input
            type="text"
            value={values.documentNumber}
            onChange={(e) => onChange('documentNumber', e.target.value)}
            placeholder={cfg.placeholders.documentNumber || ''}
            className="input text-sm w-full"
            maxLength={60}
          />
        </div>
      )}

      {needsCountry && (
        <div>
          <label className="field-label">
            {cfg.labels.issuingCountry}
            {isRequired('issuingCountry') && <span className="text-danger-500 ml-0.5">*</span>}
            <ConfidenceBadge {...getBadgeProps('issuingCountry')} />
          </label>
          <CountryCombobox
            value={values.issuingCountry}
            onChange={(code) => onChange('issuingCountry', code)}
          />
        </div>
      )}

      {(needsIssue || needsExpiry) && (
        <div className={`grid gap-3 ${needsIssue && needsExpiry ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {needsIssue && (
            <div>
              <label className="field-label">
                {cfg.labels.issueDate}
                <ConfidenceBadge {...getBadgeProps('issueDate')} />
              </label>
              <input
                type="date"
                value={values.issueDate}
                onChange={(e) => onChange('issueDate', e.target.value)}
                className="input text-sm w-full"
              />
            </div>
          )}
          {needsExpiry && (
            <div>
              <label className="field-label">
                {cfg.labels.expiryDate}
                <ConfidenceBadge {...getBadgeProps('expiryDate')} />
              </label>
              <input
                type="date"
                value={values.expiryDate}
                onChange={(e) => onChange('expiryDate', e.target.value)}
                className="input text-sm w-full"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── step bar ────────────────────────────────────────────────────────────────── */
const STEPS = ['Choose', 'Upload', 'Done'];

const StepBar = ({ current }) => (
  <div className="flex items-center gap-0 mb-6">
    {STEPS.map((label, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-2xs font-bold transition-colors
              ${done ? 'bg-success-500 text-white' : active ? 'bg-accent-600 text-white' : 'bg-ink-100 text-ink-400'}`}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : <span>{i + 1}</span>}
            </div>
            <span className={`text-xs font-semibold transition-colors
              ${active ? 'text-ink-900' : done ? 'text-success-600' : 'text-ink-400'}`}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 h-px mx-3 ${i < current ? 'bg-success-300' : 'bg-ink-200'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/* ── progress bar ────────────────────────────────────────────────────────────── */
const ProgressBar = ({ label, pct }) => (
  <div>
    <div className="flex justify-between text-xs mb-2">
      <span className="text-ink-600 font-medium">{label}</span>
      <span className="text-ink-400 tnum">{pct}%</span>
    </div>
    <div className="h-2 bg-ink-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-accent-500 rounded-full transition-all duration-500 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  </div>
);

/* ── type card ───────────────────────────────────────────────────────────────── */
const TypeCard = ({ typeKey, config, selected, onClick }) => {
  const Icon = config.icon;
  return (
    <button
      type="button"
      onClick={() => onClick(typeKey)}
      className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-center transition-all cursor-pointer
        ${selected
          ? `border-accent-500 ${config.bg} shadow-sm`
          : 'border-ink-200 bg-white hover:border-accent-300 hover:bg-accent-50/30'
        }`}
    >
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${selected ? config.bg : 'bg-ink-100'}`}>
        <Icon className={`h-4 w-4 ${selected ? config.color : 'text-ink-500'}`} />
      </div>
      <span className={`text-2xs font-semibold leading-tight ${selected ? 'text-ink-900' : 'text-ink-600'}`}>
        {config.label}
      </span>
    </button>
  );
};

/* ── main modal ──────────────────────────────────────────────────────────────── */
const UploadDocumentModal = ({ customerId, onClose, onSuccess }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  /* form state */
  const [docType, setDocType] = useState('');
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ documentNumber: '', issuingCountry: '', issueDate: '', expiryDate: '' });
  const [manuallyEditedFields, setManuallyEditedFields] = useState(new Set());

  /* flow state */
  const [step, setStep] = useState(0);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState('');
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const [pendingConfirmKey, setPendingConfirmKey] = useState(null);
  const [confirmFailed, setConfirmFailed] = useState(false);

  /* extraction state */
  const { extract, reExtractFromText, reset: resetExtraction, status: extractionStatus, result: extractionResult, error: extractionError, progress: extractionProgress } = useDocumentExtraction();
  const [typeSuggestionDismissed, setTypeSuggestionDismissed] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  /* derived */
  const cfg = DOC_CONFIG[docType];
  const accept = cfg?.accept || '.pdf,.jpg,.jpeg,.png,.doc,.docx';
  const fileSizeError = file && file.size > MAX_MB * 1024 * 1024 ? `File exceeds ${MAX_MB} MB` : null;
  const typeError = touched && !docType ? 'Please select a document type' : null;
  const fileError = touched && !file ? 'Please select a file' : null;

  const overallLowConfidence = extractionResult && extractionResult.confidence < 0.70;
  const suggestedType = extractionResult?.documentType && extractionResult.documentType !== docType
    ? extractionResult.documentType
    : null;
  const showTypeSuggestion = suggestedType && !typeSuggestionDismissed && extractionStatus === 'done';
  const unknownType = extractionStatus === 'done' && !extractionResult?.documentType && !docType;

  /* auto-fill metadata when extraction result changes */
  useEffect(() => {
    if (!extractionResult?.extractedFields) return;
    const { extractedFields } = extractionResult;
    setMeta((prev) => ({
      documentNumber: extractedFields.documentNumber?.value || prev.documentNumber,
      issuingCountry: extractedFields.issuingCountry?.value || prev.issuingCountry,
      issueDate: extractedFields.issueDate?.value || prev.issueDate,
      expiryDate: extractedFields.expiryDate?.value || prev.expiryDate,
    }));
    setManuallyEditedFields(new Set());
  }, [extractionResult]);

  const handleFileSelect = (f) => {
    setFile(f);
    setManuallyEditedFields(new Set());
    setTypeSuggestionDismissed(false);
    if (canOcr(f)) {
      extract(f, docType || null);
    }
  };

  const handleTypeChange = (t) => {
    setDocType(t);
    setError(null);
    setTypeSuggestionDismissed(false);
    if (file) {
      const ext = `.${extFromFile(file)}`;
      if (!DOC_CONFIG[t].accept.includes(ext)) {
        setFile(null);
        resetExtraction();
      } else if (extractionResult?.rawOcrText) {
        reExtractFromText(t);
        setManuallyEditedFields(new Set());
      }
    }
    setMeta({ documentNumber: '', issuingCountry: '', issueDate: '', expiryDate: '' });
  };

  const handleMetaChange = (field, value) => {
    setMeta((prev) => ({ ...prev, [field]: value }));
    setManuallyEditedFields((prev) => new Set([...prev, field]));
  };

  const buildConfirmPayload = (fileKey) => {
    const metadata = {};
    if (meta.documentNumber.trim()) metadata.documentNumber = meta.documentNumber.trim();
    if (meta.issueDate) metadata.issueDate = meta.issueDate;
    if (meta.expiryDate) metadata.expiryDate = meta.expiryDate;
    if (meta.issuingCountry) metadata.issuingCountry = meta.issuingCountry;
    return { key: fileKey, documentType: docType, metadata };
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!docType || !file || fileSizeError) return;
    setStep(1); setError(null); setConfirmFailed(false);

    try {
      setUploadLabel('Getting upload URL…'); setUploadPct(10);
      const { uploadUrl, fileKey, contentType } = await customersService.generateUploadUrl(customerId, docType, extFromFile(file));

      setUploadLabel('Uploading file…'); setUploadPct(30);
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadPct(30 + Math.round((e.loaded / e.total) * 50));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: HTTP ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(file);
      });

      setPendingConfirmKey(fileKey);
      setUploadLabel('Saving to profile…'); setUploadPct(90);

      try {
        await customersService.submitDocuments(customerId, buildConfirmPayload(fileKey));
        setUploadPct(100);
        onSuccess();
        onClose();
      } catch {
        setConfirmFailed(true);
        setError('File uploaded but could not be saved to profile. Please try again.');
        setStep(0);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Upload failed. Please try again.');
      setStep(0);
    }
  };

  const handleRetryConfirm = async () => {
    if (!pendingConfirmKey) return;
    setStep(1); setError(null); setConfirmFailed(false);
    setUploadLabel('Saving to profile…'); setUploadPct(90);
    try {
      await customersService.submitDocuments(customerId, buildConfirmPayload(pendingConfirmKey));
      setUploadPct(100);
      onSuccess();
      onClose();
    } catch (err) {
      setConfirmFailed(true);
      setError('File uploaded but could not be saved to profile. Please try again.');
      setStep(0);
    }
  };

  /* Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && step !== 1) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, step]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/50 backdrop-blur-[2px]" onClick={step === 1 ? undefined : onClose} />

      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-popover flex flex-col max-h-[92vh]">

        {/* ── header ── */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-ink-100 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-ink-900">Upload document</h2>
            <p className="text-xs text-ink-400 mt-0.5">Attach a KYC or compliance document to this customer</p>
          </div>
          {step !== 1 && (
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* ── body ── */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          <StepBar current={step} />

          {/* ── step 0: form ── */}
          {step === 0 && (
            <div className="space-y-5">
              {error && (
                <div className="flex items-start gap-2.5 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3">
                  <AlertTriangle className="h-4 w-4 text-danger-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-danger-800 flex-1">{error}</p>
                  {confirmFailed && pendingConfirmKey && (
                    <button
                      type="button"
                      onClick={handleRetryConfirm}
                      className="text-xs font-semibold text-danger-700 hover:text-danger-900 underline shrink-0"
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}

              {/* type selector */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Document type</p>
                  {typeError && <p className="text-xs text-danger-600">{typeError}</p>}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {Object.entries(DOC_CONFIG).map(([key, config]) => (
                    <TypeCard key={key} typeKey={key} config={config} selected={docType === key} onClick={handleTypeChange} />
                  ))}
                </div>
              </div>

              {/* file + metadata */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                {/* file column */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">File</p>
                    {fileError && <p className="text-xs text-danger-600">{fileError}</p>}
                    {fileSizeError && <p className="text-xs text-danger-600">{fileSizeError}</p>}
                  </div>
                  {file ? (
                    <div className="space-y-2">
                      <FilePreview file={file} onRemove={() => { setFile(null); resetExtraction(); }} />
                      <ExtractionBar status={extractionStatus} progress={extractionProgress} />

                      {/* extraction error */}
                      {extractionError && (
                        <div className="flex items-center gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2">
                          <AlertTriangle className="h-3.5 w-3.5 text-warning-600 shrink-0" />
                          <p className="text-2xs text-warning-800">OCR failed — please enter fields manually</p>
                        </div>
                      )}

                      {/* low quality warning */}
                      {overallLowConfidence && extractionStatus === 'done' && (
                        <div className="flex items-center gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2">
                          <AlertTriangle className="h-3.5 w-3.5 text-warning-600 shrink-0" />
                          <p className="text-2xs text-warning-800">Low image quality — please verify all fields</p>
                        </div>
                      )}

                      {/* unknown type alert */}
                      {unknownType && (
                        <div className="flex items-center gap-2 rounded-lg border border-info-200 bg-info-50 px-3 py-2">
                          <AlertTriangle className="h-3.5 w-3.5 text-info-600 shrink-0" />
                          <p className="text-2xs text-info-800">Could not detect document type automatically. Please select a type above.</p>
                        </div>
                      )}

                      {/* type suggestion */}
                      {showTypeSuggestion && DOC_CONFIG[suggestedType] && (
                        <div className="flex items-center justify-between gap-2 rounded-lg border border-accent-200 bg-accent-50 px-3 py-2">
                          <p className="text-2xs text-accent-800">
                            Detected: <span className="font-semibold">{DOC_CONFIG[suggestedType].label}</span> — switch to this type?
                          </p>
                          <div className="flex gap-1.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => { handleTypeChange(suggestedType); setTypeSuggestionDismissed(true); }}
                              className="text-2xs font-semibold text-accent-700 hover:text-accent-900 underline"
                            >
                              Switch
                            </button>
                            <button
                              type="button"
                              onClick={() => setTypeSuggestionDismissed(true)}
                              className="text-2xs text-ink-400 hover:text-ink-600"
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      )}

                      {/* validation warnings */}
                      {extractionStatus === 'done' && extractionResult?.validationStatus?.warnings?.length > 0 && (
                        <div className="space-y-1">
                          {extractionResult.validationStatus.warnings.map((w, i) => (
                            <div key={i} className="flex items-center gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-1.5">
                              <AlertTriangle className="h-3 w-3 text-warning-600 shrink-0" />
                              <p className="text-2xs text-warning-800">{w.message}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* debug section */}
                      {extractionStatus === 'done' && extractionResult?.rawOcrText && (
                        <div>
                          <button
                            type="button"
                            onClick={() => setShowDebug((v) => !v)}
                            className="text-2xs text-ink-400 hover:text-ink-600 underline"
                          >
                            {showDebug ? 'Hide' : 'Show'} raw OCR text
                          </button>
                          {showDebug && (
                            <pre className="mt-1.5 text-2xs text-ink-600 bg-ink-50 border border-ink-200 rounded-lg p-2 max-h-28 overflow-y-auto whitespace-pre-wrap break-all">
                              {extractionResult.rawOcrText}
                            </pre>
                          )}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="w-full text-xs text-accent-600 hover:text-accent-800 py-1.5 rounded-lg border border-accent-200 hover:bg-accent-50 transition-colors"
                      >
                        Change file
                      </button>
                      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
                    </div>
                  ) : (
                    <DropZone
                      onFile={handleFileSelect}
                      accept={accept}
                      disabled={false}
                      dragging={dragging}
                      setDragging={setDragging}
                      inputRef={inputRef}
                    />
                  )}
                </div>

                {/* metadata column */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-2">Details</p>
                  {docType ? (
                    <MetadataFields
                      docType={docType}
                      values={meta}
                      onChange={handleMetaChange}
                      extractedFields={extractionResult?.extractedFields}
                      manuallyEditedFields={manuallyEditedFields}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full min-h-[9rem] rounded-xl border-2 border-dashed border-ink-200 px-4 py-6 text-center">
                      <p className="text-xs text-ink-400">Select a document type<br />to see required fields</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── step 1: uploading ── */}
          {step === 1 && (
            <div className="flex flex-col items-center justify-center py-12 gap-6">
              <div className="relative h-20 w-20">
                <div className="absolute inset-0 rounded-full bg-accent-100 animate-ping opacity-40" />
                <div className="relative h-20 w-20 rounded-full bg-accent-50 flex items-center justify-center">
                  <Loader2 className="h-9 w-9 text-accent-600 animate-spin" />
                </div>
              </div>
              <div className="w-full max-w-sm space-y-2">
                <ProgressBar label={uploadLabel} pct={uploadPct} />
              </div>
              <p className="text-xs text-ink-400">Please keep this window open…</p>
            </div>
          )}

        </div>

        {/* ── footer ── */}
        <div className="px-6 py-4 border-t border-ink-100 bg-ink-50 rounded-b-2xl flex items-center justify-between shrink-0">
          <p className="text-2xs text-ink-400">
            {step === 0 && cfg && cfg.required.length > 0 ? (
              <span><span className="text-danger-500">*</span> Required fields</span>
            ) : null}
          </p>
          <div className="flex gap-2">
            {step === 0 && (
              <>
                <button type="button" onClick={onClose} className="btn btn-md btn-secondary">Cancel</button>
                <button type="button" onClick={handleSubmit} className="btn btn-md btn-primary">
                  <Upload className="h-4 w-4" />
                  Upload document
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentModal;
